<template>
  <CModal
    :title="`Invoice Verification for ${invoiceDetails.accountName}`"
    color="info"
    size="lg"
    :show="show"
    @update:show="$emit('update:show', $event)"
  >
    <div v-if="loading">
      <CSpinner color="info" />
      Loading invoice details...
    </div>

    <div v-else-if="invoicePreviewData">
      <!-- Toolbar -->
      <div class="d-flex justify-content-end align-items-center mb-3">
        <CButton
          size="sm"
          color="primary"
          class="mr-2"
          @click="$emit('run-verification')"
        >
          <CIcon
            name="cil-sync"
            class="mr-1"
          />
          Re-run Verification
        </CButton>
        
        <CButton
          v-if="!isVerificationSuccessful"
          size="sm"
          color="success"
          class="mr-2"
          @click="$emit('issue-invoice')"
        >
          <CIcon
            name="cil-check"
            class="mr-1"
          />
          Force Issue
        </CButton>
        
        <CButton
          size="sm"
          color="warning"
          class="mr-2 text-white"
          @click="$emit('regenerate-invoice')"
        >
          <CIcon
            name="cil-reload"
            class="mr-1"
          />
          Re-generate Invoice
        </CButton>

        <CButton
          size="sm"
          color="danger"
          class="mr-2"
          @click="$emit('delete-invoice')"
        >
          <CIcon
            name="cil-trash"
            class="mr-1"
          />
          Delete Invoice
        </CButton>
      </div>
      <div class="mb-3">
        <strong>Invoice ID:</strong> {{ paymentDetails.invoiceId }}
      </div>
      <div class="mb-3">
        <strong>Period:</strong> {{ formatDate(invoicePreviewData.startDate) }} - {{ formatDate(invoicePreviewData.endDate) }}
      </div>
      <div class="mb-3">
        <strong>Invoice Created:</strong> {{ formatDateTime(invoiceDetails.createdAtUtc || invoiceDetails.billingCycleEndDate) }}
      </div>

      <table class="table table-sm table-bordered">
        <thead class="table-info">
          <tr>
            <th>Billable Calls</th>
            <th class="text-right">
              # Calls
            </th>
            <th class="text-right">
              Amount
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="invoiceDetails.openingBalance">
            <td colspan="2">
              Opening Balance
            </td>
            <td class="text-right">
              {{ formatAmount(invoiceDetails.openingBalance) }}
            </td>
          </tr>
          <tr v-if="!invoicePreviewData.lineItems.length">
            <td>Not Applicable</td>
            <td class="text-right">
              0
            </td>
            <td class="text-right">
              {{ formatAmount(0) }}
            </td>
          </tr>
          <template v-for="(item, idx) in invoicePreviewData.lineItems">
            <tr
              :key="`item-${idx}`"
              :class="{ 'verification-error': hasLineItemError(idx, 'Amount') || hasLineItemError(idx, 'TotalCalls') }"
            >
              <td>
                <CButton
                  v-if="hasPostbackBreakdown(item)"
                  size="sm"
                  color="link"
                  class="p-0 mr-1"
                  @click="togglePostbackBreakdown(idx)"
                >
                  <CIcon :name="expandedLineItems[idx] ? 'cil-chevron-bottom' : 'cil-chevron-right'" />
                </CButton>
                {{ item.category }} - {{ item.description }}
              </td>
              <td
                class="text-right"
                :class="{ 'error-field': hasLineItemError(idx, 'TotalCalls') }"
              >
                <span v-c-tooltip="getLineItemError(idx, 'TotalCalls')">
                  <CIcon
                    v-if="hasLineItemError(idx, 'TotalCalls')"
                    name="cil-warning"
                    class="mr-1"
                  />
                  {{ item.totalCalls ? item.totalCalls.toLocaleString() : '0' }}
                </span>
              </td>
              <td
                class="text-right"
                :class="{ 'error-field': hasLineItemError(idx, 'Amount') }"
              >
                <span v-c-tooltip="getLineItemError(idx, 'Amount')">
                  <CIcon
                    v-if="hasLineItemError(idx, 'Amount')"
                    name="cil-warning"
                    class="mr-1"
                  />
                  {{ formatAmount(item.amount) }}
                </span>
              </td>
            </tr>
            <tr
              v-if="hasPostbackBreakdown(item) && expandedLineItems[idx]"
              :key="`breakdown-${idx}`"
              class="postback-breakdown-row"
            >
              <td colspan="3">
                <PostbackCallsBreakdown :calls="item.postbackCallsBreakdown" />
              </td>
            </tr>
          </template>
          <tr
            v-if="invoicePreviewData.adjustmentAmount !== 0"
            class="table-secondary"
            :class="{ 'verification-error': hasFieldError('AdjustmentAmount') }"
          >
            <td
              colspan="2"
              class="text-right"
            >
              <span
                v-c-tooltip="getFieldError('AdjustmentAmount')"
                :class="{ 'error-field': hasFieldError('AdjustmentAmount') }"
              >
                <CIcon
                  v-if="hasFieldError('AdjustmentAmount')"
                  name="cil-warning"
                  class="mr-1"
                />
                Adjustments:
              </span>
            </td>
            <td class="text-right">
              <span
                v-c-tooltip="getFieldError('AdjustmentAmount')"
                :class="{ 'error-field': hasFieldError('AdjustmentAmount') }"
              >
                {{ formatAmount(invoicePreviewData.adjustmentAmount) }}
              </span>
            </td>
          </tr>
          <tr
            v-if="invoicePreviewData.disputeFeeAmount !== 0"
            class="table-secondary"
            :class="{ 'verification-error': hasFieldError('DisputeFeeAmount') }"
          >
            <td
              colspan="2"
              class="text-right"
            >
              <span
                v-c-tooltip="getFieldError('DisputeFeeAmount')"
                :class="{ 'error-field': hasFieldError('DisputeFeeAmount') }"
              >
                <CIcon
                  v-if="hasFieldError('DisputeFeeAmount')"
                  name="cil-warning"
                  class="mr-1"
                />
                Dispute Fees:
              </span>
            </td>
            <td class="text-right">
              <span
                v-c-tooltip="getFieldError('DisputeFeeAmount')"
                :class="{ 'error-field': hasFieldError('DisputeFeeAmount') }"
              >
                {{ formatAmount(invoicePreviewData.disputeFeeAmount) }}
              </span>
            </td>
          </tr>
          <tr
            class="font-weight-bold"
            :class="{ 'verification-error': hasFieldError('AmountDue') }"
          >
            <td
              colspan="2"
              class="text-right"
            >
              <span
                v-c-tooltip="getFieldError('AmountDue')"
                :class="{ 'error-field': hasFieldError('AmountDue') }"
              >
                <CIcon
                  v-if="hasFieldError('AmountDue')"
                  name="cil-warning"
                  class="mr-1"
                />
                Total:
              </span>
            </td>
            <td class="text-right">
              <span
                v-c-tooltip="getFieldError('AmountDue')"
                :class="{ 'error-field': hasFieldError('AmountDue') }"
              >
                {{ formatAmount(invoiceTotal) }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Verification Checks Section -->
      <div
        v-if="paymentDetails.verificationChecks && paymentDetails.verificationChecks.length"
        class="mt-4"
      >
        <div class="d-flex align-items-center mb-3">
          <h6 class="mb-0">
            <CIcon
              name="cil-task"
              class="mr-2"
            />
            Verification Checks
          </h6>
        </div>
        <CCard class="mb-0">
          <CCardBody class="p-0">
            <table class="table table-sm table-hover mb-0">
              <thead class="table-light">
                <tr>
                  <th style="width: 180px">
                    Date
                  </th>
                  <th style="width: 100px">
                    Status
                  </th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(check, idx) in paymentDetails.verificationChecks"
                  :key="check.id || idx"
                >
                  <td>{{ formatCheckDate(check) }}</td>
                  <td>
                    <CBadge
                      :color="check.status === 'SUCCESS' ? 'success' : 'danger'"
                      class="px-2"
                    >
                      <CIcon
                        :name="check.status === 'SUCCESS' ? 'cil-check-circle' : 'cil-x-circle'"
                        size="sm"
                        class="mr-1"
                      />
                      {{ check.status === 'SUCCESS' ? 'Success' : 'Failed' }}
                    </CBadge>
                  </td>
                  <td>
                    <div>{{ check.description }}</div>
                    <!-- Show missing calls details if available -->
                    <div
                      v-if="getMissingCallsDetails(check)"
                      class="mt-2"
                    >
                      <CButton
                        size="sm"
                        color="info"
                        variant="outline"
                        @click="toggleMissingCallsDetails(idx)"
                      >
                        <CIcon
                          :name="expandedChecks[idx] ? 'cil-chevron-top' : 'cil-chevron-bottom'"
                          size="sm"
                          class="mr-1"
                        />
                        {{ expandedChecks[idx] ? 'Hide' : 'Show' }} Missing Calls Details
                      </CButton>
                      <div
                        v-if="expandedChecks[idx]"
                        class="missing-calls-details mt-2"
                      >
                        <table class="table table-sm table-bordered">
                          <thead class="table-secondary">
                            <tr>
                              <th>Call ID</th>
                              <th>Call Date</th>
                              <th class="text-right">
                                Amount
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="(call, callIdx) in getMissingCallsDetails(check).missingCalls"
                              :key="callIdx"
                            >
                              <td>
                                <div class="d-flex align-items-center">
                                  <code class="text-truncate call-id-cell mr-1">{{ call.CallId }}</code>
                                  <a
                                    href
                                    @click.prevent="copyTextToClipboard(call.CallId, 'Call ID copied')"
                                  >
                                    <CIcon
                                      name="cil-copy"
                                      size="sm"
                                    />
                                  </a>
                                </div>
                              </td>
                              <td>{{ formatDateTime(call.CallDate) }}</td>
                              <td class="text-right">
                                {{ formatAmount(call.CallAmount) }}
                              </td>
                            </tr>
                            <tr class="table-light font-weight-bold">
                              <td colspan="2">
                                Total
                              </td>
                              <td class="text-right">
                                {{ formatAmount(getMissingCallsDetails(check).totalMissingAmount) }}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </CCardBody>
        </CCard>
      </div>

      <!-- No Verification Checks Message -->
      <div
        v-else
        class="mt-4"
      >
        <CCard class="border-info">
          <CCardBody>
            <div class="d-flex align-items-center mb-2">
              <CIcon
                name="cil-clock"
                class="text-info mr-2"
                size="lg"
              />
              <h6 class="mb-0 text-info">
                Verification Pending
              </h6>
            </div>
            <p class="mb-2">
              The invoice generation service has not yet processed this invoice. Verification checks will appear once the invoice is fully processed.
            </p>
          </CCardBody>
        </CCard>
      </div>

      <!-- Review Notes Section -->
      <div class="mt-4 review-notes">
        <h6 class="mb-3">
          <CIcon
            name="cil-notes"
            class="mr-2"
          />
          Review Notes
        </h6>
        <ReviewNotes
          :parent-object="paymentDetailsWithId"
        />
      </div>
    </div>

    <div v-else>
      <p class="alert alert-info">
        <CIcon name="cil-info" />
        No invoice details available for this period.
      </p>
    </div>

    <template #footer>
      <CButton
        color="secondary"
        @click="$emit('update:show', false)"
      >
        Close
      </CButton>
    </template>
  </CModal>
</template>

<script>
import ReviewNotes from '@/views/ReviewNotes/ReviewNotes.vue'
import PostbackCallsBreakdown from '@/components/PostbackCallsBreakdown.vue'
import copyToClipboardMixin from '@/mixins/copy-to-clipboard-mixin'
import { formatAmount } from '@/utils'

export default {
  name: 'InvoiceVerificationModal',

  components: {
    ReviewNotes,
    PostbackCallsBreakdown
  },

  mixins: [copyToClipboardMixin],

  props: {
    show: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    invoiceDetails: {
      type: Object,
      required: true
    },
    invoicePreviewData: {
      type: Object,
      default: null
    },
    paymentDetails: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      expandedChecks: {},
      expandedLineItems: {}
    }
  },

  computed: {
    invoiceTotal() {
      if (!this.invoicePreviewData || !this.invoicePreviewData.lineItems) {
        return 0
      }

      const lineItemsTotal = this.invoicePreviewData.lineItems.reduce((sum, item) => sum + (item.isDisplayOnly ? 0 : item.amount), 0)
      
      return (this.invoiceDetails.openingBalance || 0) +
             lineItemsTotal -
             (this.invoicePreviewData.adjustmentAmount || 0) +
             (this.invoicePreviewData.disputeFeeAmount || 0)
    },

    paymentDetailsWithId() {
      const payment = this.paymentDetails || {}
      return {
        ...payment,
        id: payment.invoiceId
      }
    },

    isVerificationSuccessful() {
      return this.invoiceDetails.invoiceStatusId === 'Issued'
    },

    hasPostbackBreakdown() {
      return (lineItem) => {
        return lineItem?.postbackCallsBreakdown &&
               Array.isArray(lineItem.postbackCallsBreakdown) &&
               lineItem.postbackCallsBreakdown.length > 0
      }
    }
  },

  methods: {
    formatDate(date) {
      if (!date) {
        return 'N/A'
      }

      return new Date(date).toLocaleDateString()
    },

    formatDateTime(date) {
      if (!date) {
        return 'N/A'
      }

      return new Date(date).toLocaleString()
    },
    formatAmount,

    formatCheckDate(check) {
      const start = check.startDate ? this.formatDateTime(check.startDate) : 'N/A'
      return start
    },

    hasFieldError(field) {
      if (!this.invoicePreviewData?.errors) {
        return false
      }

      return this.invoicePreviewData.errors.some(error => error.field === field)
    },

    getFieldError(field) {
      if (!this.invoicePreviewData?.errors) {
        return null
      }

      const error = this.invoicePreviewData.errors.find(error => error.field === field)
      return error ? error.error : null
    },

    hasLineItemError(index, field) {
      if (!this.invoicePreviewData?.errors) {
        return false
      }

      const fieldName = `LineItems[${index}].${field}`
      return this.invoicePreviewData.errors.some(error => error.field === fieldName)
    },

    getLineItemError(index, field) {
      if (!this.invoicePreviewData?.errors) {
        return null
      }

      const fieldName = `LineItems[${index}].${field}`
      const error = this.invoicePreviewData.errors.find(error => error.field === fieldName)
      return error ? error.error : null
    },

    getMissingCallsDetails(check) {
      // Only show details for MISSING_CALLS verification type with failed status
      if (!check.additionalDetails || check.status === 'SUCCESS') {
        return null
      }

      try {
        const details = JSON.parse(check.additionalDetails)
        // Check if it has the expected structure for missing calls
        // Note: .NET System.Text.Json uses PascalCase by default
        if (details.MissingCalls && Array.isArray(details.MissingCalls) && details.MissingCalls.length > 0) {
          return {
            missingCalls: details.MissingCalls,
            totalMissingCalls: details.TotalMissingCalls || details.MissingCalls.length,
            totalMissingAmount: details.TotalMissingAmount || 0
          }
        }
      } catch (e) {
        console.error('Failed to parse verification check additional details:', e)
      }

      return null
    },

    toggleMissingCallsDetails(idx) {
      this.$set(this.expandedChecks, idx, !this.expandedChecks[idx])
    },

    togglePostbackBreakdown(idx) {
      this.$set(this.expandedLineItems, idx, !this.expandedLineItems[idx])
    }
  }
}
</script>

<style scoped>
.verification-error {
  background-color: #fff3cd !important;
}

.error-field {
  color: #dc3545;
  font-weight: bold;
}

.review-notes {
  border-top: 1px solid #dee2e6;
  padding-top: 1rem;
}

.missing-calls-details {
  background-color: #f8f9fa;
  padding: 0.75rem;
  border-radius: 0.25rem;
  border: 1px solid #dee2e6;
}

.missing-calls-details code {
  background-color: #e9ecef;
  padding: 0.2rem 0.4rem;
  border-radius: 0.2rem;
  font-size: 0.875rem;
}

.call-id-cell {
  max-width: 120px;
  display: inline-block;
}

.postback-breakdown-row {
  background-color: #f8f9fa;
}
</style>
